import React, { useEffect, useMemo, useRef, useState } from 'react';
import fnc from '../../mymod/commFunction';
import './WalletTransactionSummary.scss';

const SUMMARY_VIEW = {
    DAILY: 'daily',
    MONTHLY: 'monthly',
    DAILY_MAX: 'daily_max'
};

function formatAmount(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '0';

    return amount.toLocaleString('ko-KR', {
        maximumFractionDigits: 4
    });
}

function formatCount(value) {
    const count = Number(value);
    return Number.isFinite(count) ? count.toLocaleString('ko-KR') : '0';
}

function isCurrentPeriod(item) {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const periodKey = String(item?.period_key || '');

    if (item?.summary_type === SUMMARY_VIEW.DAILY) {
        return periodKey === `${year}-${month}-${day}`;
    }

    if (item?.summary_type === SUMMARY_VIEW.MONTHLY) {
        return periodKey === `${year}-${month}`;
    }

    return false;
}

function getDailyWeekday(periodKey) {
    const matched = String(periodKey || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!matched) return null;

    const year = Number(matched[1]);
    const month = Number(matched[2]);
    const day = Number(matched[3]);
    const date = new Date(year, month - 1, day);

    if (
        date.getFullYear() !== year
        || date.getMonth() !== month - 1
        || date.getDate() !== day
    ) {
        return null;
    }

    const weekdayIndex = date.getDay();
    const weekdayLabels = ['일', '월', '화', '수', '목', '금', '토'];

    return {
        label: weekdayLabels[weekdayIndex],
        className: weekdayIndex === 0
            ? 'sunday'
            : weekdayIndex === 6
                ? 'saturday'
                : ''
    };
}

const WalletTransactionSummary = ({
    searchKeyword,
    billFilter,
    excludeMathautogen,
    refreshToken,
    onTodayAmountChange,
    showTrigger = true
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeView, setActiveView] = useState(SUMMARY_VIEW.DAILY);
    const [summaryRows, setSummaryRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [retryToken, setRetryToken] = useState(0);
    const [sortConfig, setSortConfig] = useState({ key: 'period_key', direction: 'desc' });
    const wrapperRef = useRef(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        const requestId = requestIdRef.current + 1;
        let responseReceived = false;
        requestIdRef.current = requestId;
        setLoading(true);
        setErrorMessage('');
        onTodayAmountChange(null);

        const request = fnc.sendAxios('/userRoutes/selectWalletTransactionSummary', {
            searchKeyword: searchKeyword || '',
            billId: billFilter === 'all' ? '' : billFilter,
            excludedUserId: excludeMathautogen ? 'mathautogen' : ''
        }, (res) => {
            if (requestIdRef.current !== requestId) return;
            responseReceived = true;
            if (!Array.isArray(res)) {
                setLoading(false);
                setErrorMessage('집계 결과를 불러오지 못했습니다.');
                return;
            }

            const nextRows = res;
            const todaySummary = nextRows.find((item) => item?.summary_type === SUMMARY_VIEW.DAILY);

            setSummaryRows(nextRows);
            onTodayAmountChange(Number(todaySummary?.amount_total) || 0);
            setLoading(false);
        });

        Promise.resolve(request).then(() => {
            if (requestIdRef.current !== requestId || responseReceived) return;
            setLoading(false);
            setErrorMessage('집계 조회에 실패했습니다. 다시 시도해 주세요.');
        });

        const timeoutId = window.setTimeout(() => {
            if (requestIdRef.current !== requestId || responseReceived) return;
            setLoading(false);
            setErrorMessage('집계 조회 시간이 초과되었습니다.');
        }, 15000);

        return () => {
            window.clearTimeout(timeoutId);
            requestIdRef.current += 1;
        };
    }, [searchKeyword, billFilter, excludeMathautogen, refreshToken, retryToken, onTodayAmountChange]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleOutsideClick = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!showTrigger) setIsOpen(false);
    }, [showTrigger]);

    const dailyRows = useMemo(() => {
        return summaryRows.filter((item) => item?.summary_type === SUMMARY_VIEW.DAILY);
    }, [summaryRows]);

    const monthlyRows = useMemo(() => {
        return summaryRows.filter((item) => item?.summary_type === SUMMARY_VIEW.MONTHLY);
    }, [summaryRows]);

    const dailyMaxRow = useMemo(() => {
        return summaryRows.find((item) => item?.summary_type === SUMMARY_VIEW.DAILY_MAX) || null;
    }, [summaryRows]);

    function handleSort(key) {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    }

    function renderSortableHeader(label, key) {
        const isActive = sortConfig.key === key;

        return (
            <th
                className={isActive ? 'active-sort' : ''}
                onClick={() => handleSort(key)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSort(key);
                    }
                }}
                tabIndex={0}
                aria-sort={isActive ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                title="클릭해서 정렬"
            >
                {label}
            </th>
        );
    }

    function renderSummaryTable(rows, emptyText, showTotal = false) {
        if (rows.length === 0) {
            return <div className="wallet-summary-empty">{emptyText}</div>;
        }

        const currentMonthKey = showTotal ? String(rows[0]?.period_key || '').slice(0, 7) : '';
        const totalRows = showTotal
            ? rows.filter((item) => String(item?.period_key || '').startsWith(currentMonthKey))
            : rows;
        const currentMonth = Number(currentMonthKey.slice(5, 7));
        const totalLabel = Number.isFinite(currentMonth) && currentMonth > 0
            ? `${currentMonth}월 합계`
            : '이번달 합계';
        const totalCount = totalRows.reduce((total, item) => total + (Number(item?.transaction_count) || 0), 0);
        const totalAmount = totalRows.reduce((total, item) => total + (Number(item?.amount_total) || 0), 0);
        const sortedRows = rows
            .map((item, originalIndex) => ({ item, originalIndex }))
            .sort((a, b) => {
                let result;

                if (sortConfig.key === 'no') {
                    result = a.originalIndex - b.originalIndex;
                } else if (sortConfig.key === 'transaction_count' || sortConfig.key === 'amount_total') {
                    result = (Number(a.item?.[sortConfig.key]) || 0) - (Number(b.item?.[sortConfig.key]) || 0);
                } else {
                    result = String(a.item?.[sortConfig.key] || '').localeCompare(
                        String(b.item?.[sortConfig.key] || ''),
                        'ko'
                    );
                }

                return sortConfig.direction === 'asc' ? result : -result;
            })
            .map(({ item }) => item);

        return (
            <div className="wallet-summary-content">
                {showTotal && (
                    <div className="wallet-summary-highlights">
                        <div className="wallet-summary-highlight total">
                            <span>{totalLabel}</span>
                            <small>{formatCount(totalCount)}건</small>
                            <strong>{formatAmount(totalAmount)}</strong>
                        </div>
                        {dailyMaxRow && (
                            <div className="wallet-summary-highlight max">
                                <span>전체 MAX ({dailyMaxRow.period_label})</span>
                                <small>{formatCount(dailyMaxRow.transaction_count)}건</small>
                                <strong>{formatAmount(dailyMaxRow.amount_total)}</strong>
                            </div>
                        )}
                    </div>
                )}
                <div className="wallet-summary-table-wrap">
                    <table className="wallet-summary-table">
                        <thead>
                            <tr>
                                {renderSortableHeader('no', 'no')}
                                {renderSortableHeader('기간', 'period_key')}
                                {renderSortableHeader('건수', 'transaction_count')}
                                {renderSortableHeader('합계', 'amount_total')}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((item, index) => {
                                const weekday = item.summary_type === SUMMARY_VIEW.DAILY
                                    ? getDailyWeekday(item.period_key)
                                    : null;

                                return (
                                    <tr key={`${item.summary_type}-${item.period_key}`}>
                                        <td>{index + 1}</td>
                                        <td className={isCurrentPeriod(item) ? 'current-period' : ''}>
                                            {item.period_label}
                                            {weekday && (
                                                <span className={`period-weekday ${weekday.className}`.trim()}>
                                                    ({weekday.label})
                                                </span>
                                            )}
                                        </td>
                                        <td>{formatCount(item.transaction_count)}</td>
                                        <td>{formatAmount(item.amount_total)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    function renderContent() {
        if (loading) {
            return <div className="wallet-summary-empty">집계 조회 중...</div>;
        }

        if (errorMessage) {
            return (
                <div className="wallet-summary-error">
                    <span>{errorMessage}</span>
                    <button type="button" onClick={() => setRetryToken((prev) => prev + 1)}>
                        다시 시도
                    </button>
                </div>
            );
        }

        if (activeView === SUMMARY_VIEW.MONTHLY) {
            return renderSummaryTable(monthlyRows, '월별 집계 결과가 없습니다.');
        }

        return renderSummaryTable(dailyRows, '일별 집계 결과가 없습니다.', true);
    }

    if (!showTrigger) return null;

    return (
        <div className="wallet-transaction-summary" ref={wrapperRef}>
            <button
                type="button"
                className={`wallet-summary-trigger ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
            >
                집계
            </button>

            {isOpen && (
                <div className="wallet-summary-panel">
                    <div className="wallet-summary-panel-header">
                        <strong>지갑거래 집계</strong>
                        <button
                            type="button"
                            className="wallet-summary-close"
                            onClick={() => setIsOpen(false)}
                        >
                            닫기
                        </button>
                    </div>
                    <div className="wallet-summary-view-buttons">
                        <button
                            type="button"
                            className={activeView === SUMMARY_VIEW.DAILY ? 'active' : ''}
                            onClick={() => setActiveView(SUMMARY_VIEW.DAILY)}
                        >
                            일별집계
                        </button>
                        <button
                            type="button"
                            className={activeView === SUMMARY_VIEW.MONTHLY ? 'active' : ''}
                            onClick={() => setActiveView(SUMMARY_VIEW.MONTHLY)}
                        >
                            월별집계
                        </button>
                    </div>
                    {renderContent()}
                </div>
            )}
        </div>
    );
};

export default WalletTransactionSummary;
